import React from 'react';
import { adminAPI } from '../services/api';

interface Partner {
  _id: string;
  name: string;
  email: string;
  phone: string;
  vehicleType: string;
  vehicleNumber: string;
  isAvailable: boolean;
  isApproved: boolean;
  rating: number;
  totalDeliveries: number;
  earnings: {
    total: number;
    today: number;
  };
  createdAt: string;
}

interface PartnersManagementProps {
  partners: Partner[];
  onRefresh: () => void;
}

const PartnersManagement: React.FC<PartnersManagementProps> = ({ partners, onRefresh }) => {
  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this partner?')) {
      try {
        await adminAPI.deletePartner(id);
        alert('Partner deleted successfully');
        onRefresh();
      } catch (error) {
        alert('Failed to delete partner');
      }
    }
  };

  const handleApprove = async (id: string) => {
    try {
      await adminAPI.approvePartner(id);
      alert('Partner approved successfully');
      onRefresh();
    } catch (error) {
      alert('Failed to approve partner');
    }
  };

  const handleReject = async (id: string) => {
    if (window.confirm('Are you sure you want to revoke approval for this partner?')) {
      try {
        await adminAPI.rejectPartner(id);
        alert('Partner approval revoked');
        onRefresh();
      } catch (error) {
        alert('Failed to revoke approval');
      }
    }
  };

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Partners Management</h2>
      </div>

      {partners.length === 0 ? (
        <div className="empty-state">
          <h3>No Partners Found</h3>
          <p>There are no registered delivery partners in the system.</p>
        </div>
      ) : (
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Vehicle</th>
                <th>Approval</th>
                <th>Status</th>
                <th>Rating</th>
                <th>Deliveries</th>
                <th>Earnings (+ Tips)</th>
                <th>Total Income</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {partners.map((partner) => (
                <tr key={partner._id}>
                  <td>{partner.name}</td>
                  <td>{partner.email}</td>
                  <td>{partner.phone}</td>
                  <td>{partner.vehicleType} - {partner.vehicleNumber}</td>
                  <td>
                    <span className={`status-badge ${partner.isApproved ? 'status-delivered' : 'status-pending'}`}>
                      {partner.isApproved ? '✅ Approved' : '⏳ Pending'}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${partner.isAvailable ? 'status-delivered' : 'status-cancelled'}`}>
                      {partner.isAvailable ? 'Available' : 'Offline'}
                    </span>
                  </td>
                  <td>⭐ {partner.rating.toFixed(1)}</td>
                  <td>{partner.totalDeliveries}</td>
                  <td>
                    <div style={{fontWeight: 'bold'}}>₹{partner.earnings.total.toFixed(2)}</div>
                    <small style={{color: '#28a745'}}>💰 Tips: ₹{(partner.earnings as any).tips || 0}</small>
                  </td>
                  <td style={{color: '#60b246', fontWeight: 'bold', fontSize: '16px'}}>
                    ₹{(partner.earnings.total + ((partner.earnings as any).tips || 0)).toFixed(2)}
                  </td>
                  <td>
                    {!partner.isApproved ? (
                      <button className="action-btn" style={{background: '#60b246', marginRight: '5px'}} onClick={() => handleApprove(partner._id)}>
                        ✅ Approve
                      </button>
                    ) : (
                      <button className="action-btn" style={{background: '#ffa500', marginRight: '5px'}} onClick={() => handleReject(partner._id)}>
                        ⏸️ Suspend
                      </button>
                    )}
                    <button className="action-btn delete-btn" onClick={() => handleDelete(partner._id)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PartnersManagement;
